import React, { useState, useEffect } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardDrive, CheckCircle2, RefreshCw, Loader2, ShieldCheck, Lock, Eye, EyeOff, KeyRound, History, PieChart, ChevronDown, ChevronRight } from "lucide-react";

function DriveTestAccess({ account }) {
  const [folder, setFolder] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/drive/test-access", { params: { folder_id: folder.trim() } }); setRes(r.data); }
    catch (e) { setRes({ ok: false, error: e.response?.data?.detail || "Could not test access" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="drive-test-access">
      <div className="text-xs text-slate-600">Share any Drive folder (e.g. the estimating workspace <b>PR-ESTIMATING</b>) read-only with <span className="font-mono break-all">{account}</span> once, then every file in it can be filed from the connector (add_document_from_url, ingest_drive_folder).</div>
      <div className="flex gap-2 flex-wrap">
        <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Folder id from the Drive link (optional)" className="flex-1 min-w-[240px] text-sm border border-slate-200 rounded px-3 py-1.5" data-testid="drive-test-folder" />
        <Button variant="outline" onClick={run} disabled={busy} data-testid="drive-test-access-btn">{busy ? "Checking…" : "Test access"}</Button>
      </div>
      {res && (
        <div className="text-xs" data-testid="drive-test-result">
          {!res.ok ? <span className="text-rose-700">{res.error}</span> : res.folder ? (
            <><div className="text-emerald-700">Can read <b>{res.folder.name}</b> · {res.count} file(s)</div>
              <ul className="mt-1 max-h-40 overflow-auto font-mono text-[11px] text-slate-600">{(res.files || []).map((f) => <li key={f.file_id}>{f.rel ? `${f.rel}/` : ""}{f.name}</li>)}</ul></>
          ) : (
            <><div className="text-emerald-700">Shared with the account: {(res.shared_with_me || []).length} item(s)</div>
              <ul className="mt-1 max-h-40 overflow-auto font-mono text-[11px] text-slate-600">{(res.shared_with_me || []).map((f) => <li key={f.id}>{f.folder ? "📁 " : ""}{f.name} <span className="text-slate-400">{f.id}</span></li>)}</ul></>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminStorage() {
  const [st, setSt] = useState(null);
  const [sa, setSa] = useState("");
  const [driveId, setDriveId] = useState("");
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [mig, setMig] = useState(null);
  const [editing, setEditing] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [acts, setActs] = useState([]);
  const [usage, setUsage] = useState(null);
  const [recalcing, setRecalcing] = useState(false);

  const load = () => api.get("/storage/drive/status").then((r) => { setSt(r.data); setMig(r.data.migration); }).catch(() => {});
  const loadActs = () => api.get("/storage/drive/activity").then((r) => setActs(r.data || [])).catch(() => {});
  const loadUsage = () => api.get("/storage/drive/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { load(); loadActs(); }, []);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("drive");
    if (!p) return;
    if (p === "connected") toast.success("Google Drive connected — click ‘Use my Google Drive’ to turn on storage.");
    else if (p === "scope") toast.error("Drive access wasn't granted. Please try again and allow Drive access.");
    else if (p === "error") toast.error("Could not connect Google Drive. Please try again.");
    window.history.replaceState({}, "", "/admin/storage");
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (st?.enabled) loadUsage(); }, [st?.enabled]);

  const pollMig = () => api.get("/storage/drive/migrate/status").then((r) => {
    setMig(r.data);
    if (r.data?.status === "running") setTimeout(pollMig, 2000);
  }).catch(() => {});

  const connectDrive = () => { window.location.href = `${API_BASE}/oauth/drive/login`; };
  const connectOauth = async () => {
    setConnecting(true);
    try {
      const r = await api.post("/storage/drive/connect-oauth");
      setSt((s) => ({ ...s, ...r.data })); load(); loadActs();
      toast.success("Google Drive connected — new files now save to your Drive");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect your Google Drive"); }
    finally { setConnecting(false); }
  };
  const enable = async () => {
    if (!sa.trim() || !driveId.trim()) { toast.error("Paste the service-account JSON and the Shared Drive ID."); return; }
    setSaving(true);
    try {
      const r = await api.post("/storage/drive/config", { service_account: sa, shared_drive_id: driveId.trim() });
      setSt(r.data); setSa(""); setDriveId(""); setEditing(false); setReveal(false); loadActs();
      toast.success(`Connected to "${r.data.shared_drive_name}"`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not enable Drive storage"); }
    finally { setSaving(false); }
  };
  const reEnable = async () => {
    setSaving(true);
    try { const r = await api.post("/storage/drive/enable"); setSt(r.data); toast.success("Drive storage re-enabled"); loadActs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not re-enable"); }
    finally { setSaving(false); }
  };
  const migrate = async () => {
    try { await api.post("/storage/drive/migrate"); toast.success("Migration started — moving existing files to Drive"); setMig({ status: "running", done: 0, total: 0 }); pollMig(); loadActs(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start migration"); }
  };
  const disable = async () => {
    if (!window.confirm("Turn off Drive storage? New files go back to managed storage. Files already in Drive stay accessible.")) return;
    try { const r = await api.post("/storage/drive/disable"); setSt(r.data); toast.success("Drive storage disabled"); loadActs(); }
    catch { toast.error("Could not disable"); }
  };
  const recalc = async () => {
    setRecalcing(true);
    try { const r = await api.post("/storage/drive/usage/recalc"); await loadUsage(); toast.success(`Sized ${r.data.updated} file${r.data.updated === 1 ? "" : "s"}`); }
    catch { toast.error("Could not recalculate sizes"); }
    finally { setRecalcing(false); }
  };

  const migRunning = mig?.status === "running";
  const pct = mig?.total ? Math.round((mig.done / mig.total) * 100) : 0;
  const isOauth = st?.mode === "oauth";
  const isSa = st?.mode === "service_account";
  const showSaForm = advanced || editing;

  return (
    <div className="max-w-[1500px] w-full mx-auto p-6" data-testid="admin-storage-page">
      <div className="flex items-center gap-2 mb-1">
        <HardDrive className="w-5 h-5 text-[#003E87]" />
        <h1 className="text-lg font-semibold text-black">File Storage</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Store every project's documents, plan sets, proposals and ZIPs in Google Drive. Files stay browsable natively in Drive and are streamed through the app so permissions and sent-bid locks still apply.</p>

      <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-5" data-testid="admin-storage-security-note">
        <ShieldCheck className="w-4 h-4 text-[#003E87] shrink-0" />
        <span>Owner-only. Connect with Google in one click — no keys to paste. We only touch the files this app creates in your Drive.</span>
      </div>

      <Card className="p-6" data-testid="drive-storage-card">
        {!st ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : (
          <div className="space-y-4">
            {/* Connected — OAuth */}
            {st.enabled && isOauth && (
              <>
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="drive-oauth-connected">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>Connected with Google as <b>{st.oauth_email || st.google_email}</b> · files save under <b>{st.root_folder}</b> in your Drive.</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button data-testid="drive-migrate-btn" onClick={migrate} disabled={migRunning} variant="outline">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${migRunning ? "animate-spin" : ""}`} /> {migRunning ? "Migrating…" : "Migrate existing files to Drive"}
                  </Button>
                  <button data-testid="drive-disable-btn" onClick={disable} className="text-xs text-slate-400 hover:text-rose-600">Turn off</button>
                </div>
              </>
            )}

            {/* Connected — service account (legacy) */}
            {st.enabled && isSa && (
              <>
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="drive-connected">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>Connected to <b>{st.shared_drive_name}</b> · files save under <b>{st.root_folder}</b>.</span>
                </div>
                <div className="text-xs text-slate-500 font-mono break-all">Service account: {st.client_email}</div>
                <DriveTestAccess account={st.client_email} />
                <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="drive-key-masked">
                  <Lock className="w-3.5 h-3.5 text-slate-400" /> Service-account key stored — <span className="font-mono">••••••••••••</span> (hidden)
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Button data-testid="drive-migrate-btn" onClick={migrate} disabled={migRunning} variant="outline">
                    <RefreshCw className={`w-4 h-4 mr-1.5 ${migRunning ? "animate-spin" : ""}`} /> {migRunning ? "Migrating…" : "Migrate existing files to Drive"}
                  </Button>
                  <Button data-testid="drive-replace-key-btn" onClick={() => setEditing((e) => !e)} variant="outline">
                    <KeyRound className="w-4 h-4 mr-1.5" /> {editing ? "Cancel key change" : "Replace key"}
                  </Button>
                  <button data-testid="drive-disable-btn" onClick={disable} className="text-xs text-slate-400 hover:text-rose-600">Turn off</button>
                </div>
                {mig && mig.status !== "idle" && (
                  <div className="text-xs text-slate-500" data-testid="drive-migrate-status">
                    {migRunning
                      ? <>Migrating {mig.done}/{mig.total} ({pct}%)…</>
                      : mig.status === "done"
                        ? <span className="text-emerald-700">Migration complete — {mig.migrated} file{mig.migrated === 1 ? "" : "s"} moved to Drive.</span>
                        : mig.status === "error"
                          ? <span className="text-rose-600">Migration error: {mig.error}</span>
                          : null}
                  </div>
                )}
              </>
            )}

            {/* Stored-but-off */}
            {!st.enabled && st.configured && !editing && (
              <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="drive-disabled-stored">
                <span className="text-sm text-amber-800 flex items-center gap-2"><Lock className="w-4 h-4 shrink-0" /> Storage is set up but currently <b>off</b>.</span>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={reEnable} disabled={saving} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold" data-testid="drive-reenable-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Re-enable"}
                  </Button>
                </div>
              </div>
            )}

            {/* Not connected → independent Google Drive consent (drive.file only, separate from Gmail) */}
            {!st.enabled && !st.configured && (
              <div className="space-y-3" data-testid="drive-connect-block">
                {st.google_can_drive ? (
                  <>
                    <p className="text-sm text-slate-600">Google Drive is connected{st.google_email ? <> as <b>{st.google_email}</b></> : ""} and Drive access is granted. Turn on Drive storage in one click.</p>
                    <Button onClick={connectOauth} disabled={connecting} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold" data-testid="drive-connect-oauth">
                      {connecting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Connecting…</> : <><HardDrive className="w-4 h-4 mr-1.5" /> Use my Google Drive</>}
                    </Button>
                    <button onClick={connectDrive} className="block text-[11px] text-slate-400 hover:text-[#003E87]" data-testid="drive-reconnect">Connect a different Google account</button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-slate-600">Connect your Google Drive to save project files straight to your own Drive. This is a <b>separate</b> one-tap sign-in from email — it only asks for Drive access and never touches your inbox connection.</p>
                    <Button onClick={connectDrive} className="bg-[#003E87] hover:bg-[#00306a] text-white" data-testid="drive-connect-google">
                      <HardDrive className="w-4 h-4 mr-1.5" /> Connect Google Drive
                    </Button>
                    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-500">
                      First time you may see "Google hasn't verified this app" — click <b>Advanced</b> → <b>Continue</b>. You can disconnect anytime.
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Advanced: service-account key */}
            {!(st.enabled && isSa) && (
              <div className="pt-1">
                <button type="button" onClick={() => setAdvanced((a) => !a)} data-testid="drive-advanced-toggle"
                  className="text-xs text-slate-400 hover:text-[#003E87] flex items-center gap-1">
                  {advanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Advanced: use a service-account key instead
                </button>
              </div>
            )}

            {showSaForm && (
              <div className="space-y-3 max-w-xl border-t border-slate-100 pt-4" data-testid="drive-config-form">
                <p className="text-[11px] text-slate-400">For teams that store files in a shared company Drive via a Google Cloud service account. Most people should use one-click Google above.</p>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Service-account JSON key <span className="text-slate-400 font-normal">· write-only</span></Label>
                    <button type="button" onClick={() => setReveal((r) => !r)} className="text-[11px] text-slate-400 hover:text-[#003E87] flex items-center gap-1" data-testid="drive-sa-reveal">
                      {reveal ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Show while pasting</>}
                    </button>
                  </div>
                  <textarea data-testid="drive-sa-input" value={sa} onChange={(e) => setSa(e.target.value)} rows={5}
                    placeholder="Paste the full contents of the service-account .json key here"
                    style={reveal ? undefined : { WebkitTextSecurity: "disc" }}
                    className="w-full mt-1 text-xs font-mono border border-slate-200 rounded-lg px-3 py-2 focus:border-[#003E87] outline-none" />
                  <span className="text-[11px] text-slate-400">Pasted once, then cleared — we never send it back to the browser.</span>
                </div>
                <div>
                  <Label className="text-xs">Shared Drive ID</Label>
                  <Input data-testid="drive-id-input" value={driveId} onChange={(e) => setDriveId(e.target.value)} placeholder="e.g. 0ABcdEfGh..." className="font-mono" />
                  <span className="text-[11px] text-slate-400">From the Shared Drive URL: drive.google.com/drive/folders/<b>THIS_ID</b>. Add the service-account email as a Manager of that Shared Drive first.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button data-testid="drive-enable-btn" onClick={enable} disabled={saving} className="bg-[#F4C400] hover:bg-[#D9AE00] text-black font-semibold">
                    {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Testing…</> : (editing ? "Save new key & reconnect" : "Test & enable service-account storage")}
                  </Button>
                  {(editing || advanced) && <Button variant="outline" onClick={() => { setEditing(false); setAdvanced(false); setSa(""); setDriveId(""); setReveal(false); }} data-testid="drive-form-cancel">Cancel</Button>}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {st?.enabled && usage && (
        <Card className="p-5 mt-4" data-testid="drive-usage-panel">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2"><PieChart className="w-4 h-4 text-[#003E87]" /><h2 className="text-sm font-semibold text-black">Storage usage</h2></div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Total <b className="font-mono text-slate-800" data-testid="usage-total">{fmtBytes(usage.total_bytes)}</b> · {usage.total_files} file{usage.total_files === 1 ? "" : "s"}</span>
              <Button size="sm" variant="outline" onClick={recalc} disabled={recalcing} data-testid="usage-recalc-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${recalcing ? "animate-spin" : ""}`} /> Recalculate
              </Button>
            </div>
          </div>
          {usage.missing_sizes > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5 mb-3" data-testid="usage-missing-note">
              {usage.missing_sizes} older file{usage.missing_sizes === 1 ? " has" : "s have"} no size recorded yet — click <b>Recalculate</b> to pull exact sizes from Drive.
            </div>
          )}
          {usage.projects.length === 0 ? (
            <div className="text-xs text-slate-400">No project files in Drive yet.</div>
          ) : (
            <div className="space-y-2">
              {usage.projects.slice(0, 20).map((p) => {
                const top = usage.projects[0]?.bytes || 1;
                const w = Math.max(2, Math.round((p.bytes / top) * 100));
                return (
                  <div key={p.bid_id} className="group" data-testid={`usage-project-${p.bid_id}`}>
                    <div className="flex items-center justify-between gap-3 text-xs mb-0.5">
                      <a href={`/bids/${p.bid_id}`} className="truncate text-slate-700 hover:text-[#003E87] font-medium" title={p.name}>{p.name}</a>
                      <span className="shrink-0 text-slate-500 font-mono">{fmtBytes(p.bytes)} · {p.files}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-[#003E87] rounded-full" style={{ width: `${w}%` }} />
                    </div>
                  </div>
                );
              })}
              {usage.system?.files > 0 && (
                <div className="flex items-center justify-between gap-3 text-[11px] text-slate-400 pt-1 border-t border-slate-100" data-testid="usage-system">
                  <span>System (renders, thumbnails, avatars)</span>
                  <span className="font-mono">{fmtBytes(usage.system.bytes)} · {usage.system.files}</span>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {acts.length > 0 && (
        <Card className="p-5 mt-4" data-testid="drive-activity-log">
          <div className="flex items-center gap-2 mb-3"><History className="w-4 h-4 text-[#003E87]" /><h2 className="text-sm font-semibold text-black">Storage activity</h2></div>
          <div className="divide-y divide-slate-100">
            {acts.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 text-xs" data-testid={`drive-activity-${i}`}>
                <div className="min-w-0">
                  <div className="text-slate-700 truncate">{a.message}</div>
                  <div className="text-[11px] text-slate-400">{a.user_name || "—"}</div>
                </div>
                <div className="text-[11px] text-slate-400 shrink-0" title={a.at}>{timeAgo(a.at)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
}
